import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FaPlus,
  FaSearch,
  FaShieldAlt,
  FaSyncAlt,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import {
  FiEdit2,
  FiTrash2,
} from "react-icons/fi";
import { API_BASE } from "../../../ApiUrl";
import "./Roles.css";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [saving, setSaving] = useState(false);

  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [formData, setFormData] = useState({
    code: "",
    name: "",
  });

  const fetchRoles = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${API_BASE}/roles/`);

      console.log("Roles API Response:", response.data);

      let roleData = [];

      if (response.data.success && response.data.data) {
        roleData = Array.isArray(response.data.data)
          ? response.data.data
          : [response.data.data];
      } else if (Array.isArray(response.data)) {
        roleData = response.data;
      } else {
        roleData = [response.data];
      }

      const superAdminRoles = roleData.filter(
        (role) => role.roles_for?.toLowerCase() === "super admin"
      );

      setRoles(superAdminRoles);
      setFilteredRoles(superAdminRoles);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching roles:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          "Failed to load roles. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredRoles(roles);
      setCurrentPage(1);
      return;
    }

    const term = searchTerm.toLowerCase();

    const filtered = roles.filter(
      (role) =>
        role.code?.toLowerCase().includes(term) ||
        role.name?.toLowerCase().includes(term) ||
        role.roles_for?.toLowerCase().includes(term)
    );

    setFilteredRoles(filtered);
    setCurrentPage(1);
  }, [searchTerm, roles]);

  const formatDate = (dateString) => {
    if (!dateString) return "—";

    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalPages = Math.ceil(filteredRoles.length / entriesPerPage);

  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;

  const paginatedRoles = filteredRoles.slice(startIndex, endIndex);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }

      return pages;
    }

    pages.push(1);

    if (currentPage > 3) {
      pages.push("left-dots");
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) {
      pages.push("right-dots");
    }

    pages.push(totalPages);

    return pages;
  };

  const openAddModal = () => {
    setEditingRole(null);

    setFormData({
      code: "",
      name: "",
    });

    setShowModal(true);
  };

  const openEditModal = (role) => {
    setEditingRole(role);

    setFormData({
      code: role.code || "",
      name: role.name || "",
    });

    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const submitRoleForm = async (e) => {
    e.preventDefault();

    if (!formData.code.trim() || !formData.name.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing fields",
        text: "Role code and role name are both required.",
      });

      return;
    }

    setSaving(true);

    try {
      const payload = {
        code: formData.code.trim(),
        name: formData.name.trim(),
        roles_for: "super admin",
        tenant: null,
      };

      if (editingRole) {
        await axios.put(`${API_BASE}/roles/${editingRole.id}/`, payload);

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Role updated successfully!",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        await axios.post(`${API_BASE}/roles/`, payload);

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Role created successfully!",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      setShowModal(false);
      fetchRoles();
    } catch (error) {
      console.error("Error saving role:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          "Failed to save role. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async (role) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete the "${role.name}" role? This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete role",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE}/roles/${role.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Role has been deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      fetchRoles();
    } catch (error) {
      console.error("Error deleting role:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          "Failed to delete role. Please try again.",
      });
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="role-table-page-content">
          <div className="role-table-header">
            <div className="role-table-header-left">
              <h2>Role Management</h2>

              <span className="role-table-count">
                {roles.length} roles
              </span>
            </div>

            <div className="role-table-header-right">
              <div className="role-table-search-wrapper">
                <FaSearch className="role-table-search-icon" />

                <input
                  type="text"
                  placeholder="Search roles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="role-table-search-input"
                />
              </div>

              <button
                className="role-table-refresh-btn"
                onClick={fetchRoles}
                title="Refresh"
              >
                <FaSyncAlt />
              </button>

              <button
                className="role-table-add-btn"
                onClick={openAddModal}
              >
                <FaPlus />
                Add Role
              </button>
            </div>
          </div>

          {loading ? (
            <div className="role-table-loader-container">
              <div className="role-table-loader">
                <div className="role-table-spinner"></div>
                <p>Loading roles...</p>
              </div>
            </div>
          ) : (
            <div className="role-table-container">
              <table className="role-table">
                <thead>
                  <tr>
                    
                    <th>Role Name</th>
                    <th>Role Code</th>
                    <th>Created</th>
                    <th className="role-table-actions-header">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedRoles.length > 0 ? (
                    paginatedRoles.map((role) => (
                      <tr key={role.id}>
                        

                        <td className="role-table-name-cell">
                          <div className="role-table-name-wrapper">
                            <div className="role-table-avatar">
                              {role.name?.charAt(0).toUpperCase()}
                            </div>

                            <span>{role.name}</span>
                          </div>
                        </td>
                        <td>
                          <span className="role-table-code-badge">
                            {role.code}
                          </span>
                        </td>

                        <td>
                          <span className="role-table-created-text">
                            {formatDate(role.created)}
                          </span>
                        </td>

                        <td>
                          <div className="role-table-actions-cell">
                            <button
                              className="role-table-action-btn role-table-edit-btn"
                              onClick={() => openEditModal(role)}
                              title="Edit Role"
                            >
                              <FiEdit2 />
                            </button>

                            <button
                              className="role-table-action-btn role-table-delete-btn"
                              onClick={() => deleteRole(role)}
                              title="Delete Role"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="4"
                        className="role-table-no-data"
                      >
                        <div className="role-table-empty-state">
                          <div className="role-table-empty-icon">
                            <FaShieldAlt />
                          </div>

                          <p>No roles found</p>

                          <span className="role-table-empty-sub">
                            {searchTerm
                              ? "Try adjusting your search"
                              : "Add your first role to get started"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {filteredRoles.length > 0 && (
                <div className="role-table-pagination">
                  <div className="role-table-show-entries">
                    <span>Show</span>

                    <select
                      value={entriesPerPage}
                      onChange={handleEntriesChange}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>

                    <span>entries</span>
                  </div>

                  <div className="role-table-pagination-info">
                    Showing <strong>{startIndex + 1}</strong> to{" "}
                    <strong>
                      {Math.min(endIndex, filteredRoles.length)}
                    </strong>{" "}
                    of <strong>{filteredRoles.length}</strong> roles
                  </div>

                  <div className="role-table-pagination-controls">
                    <button
                      className="role-table-page-btn role-table-prev-btn"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      title="Previous"
                    >
                      <FaChevronLeft />
                    </button>

                    <div className="role-table-page-numbers">
                      {getPageNumbers().map((page, index) =>
                        typeof page === "string" ? (
                          <span
                            key={`${page}-${index}`}
                            className="role-table-page-dots"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            className={`role-table-page-btn ${
                              currentPage === page ? "active" : ""
                            }`}
                            onClick={() => goToPage(page)}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      className="role-table-page-btn role-table-next-btn"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      title="Next"
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div
          className="role-table-modal-overlay"
          onClick={() => !saving && setShowModal(false)}
        >
          <div
            className="role-table-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="role-table-modal-header">
              <h2>
                {editingRole ? "Edit Role" : "Add New Role"}
              </h2>

              <button
                className="role-table-modal-close"
                onClick={() => setShowModal(false)}
                disabled={saving}
              >
                ×
              </button>
            </div>

            <form
              onSubmit={submitRoleForm}
              className="role-table-modal-form"
            >
              <div className="role-table-form-group">
                <label>Role Code</label>

                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="e.g. admin"
                  required
                  autoFocus
                />
              </div>

              <div className="role-table-form-group">
                <label>Role Name</label>

                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. Admin"
                  required
                />
              </div>

              <div className="role-table-modal-actions">
                <button
                  type="button"
                  className="role-table-cancel-btn"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="role-table-submit-btn"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editingRole
                    ? "Update Role"
                    : "Add Role"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;